import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font, Image } from '@react-pdf/renderer';

// Register a standard font that supports markdown-like bold (optional but good for polished look)
// Font.register({
//   family: 'Helvetica',
//   fonts: [
//     { src: 'https://fonts.gstatic.com/s/helveticaneue/v70/1Ptsg8zYS_SKggPNyCg4QxlF.ttf' },
//     { src: 'https://fonts.gstatic.com/s/helveticaneue/v70/1Ptsg8zYS_SKggPNyCg4QxlF.ttf', fontWeight: 'bold' }
//   ]
// });

// Parse simple markdown-like bold syntax (**text**) for rendering
const FormattedText = ({ text, style }) => {
    if (!text) return null;
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return (
        <Text style={style}>
            {parts.map((part, i) => {
                if (part.startsWith('**') && part.endsWith('**')) {
                    return <Text key={i} style={{ fontFamily: 'Helvetica-Bold' }}>{part.slice(2, -2)}</Text>;
                }
                return part;
            })}
        </Text>
    );
};

const MultiPagePdfDocument = ({ template, fieldValues, letterBody }) => {
    const config = template?.designConfig || {};
    const header = template?.headerConfig || {};
    const footer = template?.footerConfig || {};

    const styles = StyleSheet.create({
        page: {
            paddingTop: config.marginTop || 40,
            paddingBottom: config.marginBottom || 60,
            paddingLeft: config.marginLeft || 40,
            paddingRight: config.marginRight || 40,
            fontFamily: config.fontFamily || 'Helvetica',
            fontSize: config.fontSize || 11,
            color: '#000000',
        },
        header: {
            marginBottom: 30,
            flexDirection: 'row',
            alignItems: 'center',
        },
        headerLeft: {
            width: header.logoWidth || 120,
            height: header.logoHeight || 60,
            justifyContent: 'center',
            alignItems: 'center',
        },
        headerRight: {
            marginLeft: 20,
            flex: 1,
        },
        companyName: {
            fontSize: 16,
            fontFamily: 'Helvetica-Bold',
            marginBottom: 4,
        },
        companyAddress: {
            fontSize: 9,
            color: '#4b5563',
            marginBottom: 2,
            lineHeight: 1.3
        },
        contactDetails: {
            fontSize: 9,
            color: '#4b5563',
        },
        body: {
            lineHeight: 1.6,
            textAlign: 'left',
        },
        paragraph: {
            marginBottom: 8,
        },
        footer: {
            position: 'absolute',
            bottom: 30,
            left: config.marginLeft || 40,
            right: config.marginRight || 40,
            flexDirection: 'row',
            justifyContent: 'space-between',
            borderTopWidth: 1,
            borderTopColor: '#e5e7eb',
            paddingTop: 8,
        },
        footerText: {
            fontSize: 8,
            color: '#6b7280',
        },
        pageNumber: {
            fontSize: 8,
            color: '#6b7280',
        }
    });

    // Replace placeholders with real values
    const replacePlaceholders = (text) => {
        if (!text) return "";
        let result = text;
        const fields = template?.fields || [];
        fields.forEach(f => {
            const val = fieldValues[f.key] || `[${f.label}]`;
            result = result.replace(new RegExp(`{{${f.key}}}`, 'g'), val);
        });
        return result;
    };

    const finalBody = replacePlaceholders(letterBody || template?.defaultBody || '');
    const paragraphs = finalBody.split('\n');

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                
                {/* HEADER (only on first page for cleaner multi-page flow) */}
                <View style={[styles.header, 
                    header.alignment === 'center' ? { flexDirection: 'column', alignItems: 'center' } :
                    header.alignment === 'left' ? { flexDirection: 'column', alignItems: 'flex-start' } :
                    header.alignment === 'right' ? { flexDirection: 'column', alignItems: 'flex-end' } :
                    { justifyContent: 'flex-start' } // default 'split' (side-by-side)
                ]}>
                    <View style={[!header.logoUrl && { backgroundColor: '#16a34a', width: 60, height: 60, justifyContent: 'center', alignItems: 'center' }, header.alignment !== 'split' ? { marginBottom: 10 } : { marginRight: 15 }]}>
                        {header.logoUrl ? (
                            <Image src={header.logoUrl} style={{ height: header.logoHeight || 60 }} />
                        ) : (
                            <Text style={{ color: '#ffffff', fontSize: 30, fontFamily: 'Helvetica-Bold' }}>G</Text>
                        )}
                    </View>
                    <View style={[
                        styles.headerRight, 
                        header.alignment === 'split' ? {
                            alignItems: 'flex-start',
                            textAlign: 'left',
                            marginLeft: 0
                        } : {
                            marginLeft: 0, 
                            alignItems: header.alignment === 'center' ? 'center' : header.alignment === 'right' ? 'flex-end' : 'flex-start',
                            textAlign: header.alignment === 'center' ? 'center' : header.alignment === 'right' ? 'right' : 'left'
                        }
                    ]}>
                        <Text style={styles.companyName}>{header.companyName}</Text>
                        <Text style={styles.companyAddress}>{header.companyAddress}</Text>
                        <Text style={styles.contactDetails}>{header.contactDetails}</Text>
                    </View>
                </View>

                {/* DYNAMIC CONTENT (auto flows across pages) */}
                <View style={styles.body}>
                    {paragraphs.map((p, index) => {
                        let align = 'left';
                        let content = p;

                        if (content.match(/<center>/i)) {
                            align = 'center';
                            content = content.replace(/<\/?center>/gi, '');
                        } else if (content.match(/<right>/i)) {
                            align = 'right';
                            content = content.replace(/<\/?right>/gi, '');
                        }

                        return (
                            <View key={index} style={[
                                p === '' ? { height: 4 } : styles.paragraph,
                                align !== 'left' && { textAlign: align }
                            ]}>
                                <FormattedText text={content} />
                            </View>
                        );
                    })}

                    {/* SIGNATURE (Renders at the end of the text, flowing naturally) */}
                    {footer.signatureUrl && (
                        <View style={{ marginTop: 10 }}>
                            <Image src={footer.signatureUrl} style={{ width: footer.signatureWidth || 100, height: footer.signatureHeight || 40, marginBottom: 5, objectFit: 'contain' }} />
                            <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 10 }}>Authorized Signatory</Text>
                        </View>
                    )}
                </View>

                {/* FIXED FOOTER (repeats on every page) */}
                <View style={styles.footer} fixed>
                    <Text style={styles.footerText}>{footer.text}</Text>
                    {footer.showPageNumber && (
                        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => (
                            totalPages > 1 ? `Page ${pageNumber} of ${totalPages}` : ''
                        )} />
                    )}
                </View>

            </Page>
        </Document>
    );
};

export default MultiPagePdfDocument;
